import React, { useRef, useState, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { Dropdown, DropdownAnchor, DropdownContent, DropdownMenu, MenuItem } from '@fiverr-private/dropdown';
import { Row } from '@fiverr-private/briefs_table';
import { EllipsisIcon } from '@fiverr-private/visuals';
import { IconButton } from '@fiverr-private/button';
import { isBriefActive } from '@fiverr-private/dynamic_matching';
import {
  CloseAction,
  CloseBriefButton,
  CloseBriefModal,
  getCloseAction,
  useCloseBriefModal,
} from '@fiverr-private/close_brief';
import { Container } from '@fiverr-private/layout_components';
import { raiseToast } from '@fiverr-private/toast';
import { EditBriefDrawer, useEnrichedBuyerBrief, useEnrichedDuplicateBrief } from '@fiverr-private/dm_brief_creation';
import { AppI18n, appTranslate } from '../../../../i18n';
import { BriefItem } from '../../../../../lib/types/brief';
import { useInitialProps } from '../../../../context';
import { metric } from '../../../../../lib/metric';
import { isCurrentUser } from '../../../../../lib/utils/getters';
import { BRIEFS_PAGINATED_LIST_QUERY_KEY } from '../../../shared/const';
import { PAGE_NAME } from '../../../../../../configuration/constants';
import { reportBriefCardClick } from '../../../shared/bqEvents';
import { useBriefDrawer } from '../../useBriefDrawer';
import { MoreOptionsWrapper } from './MoreOptionsWrapper';

interface MoreProps {
  row: Row<BriefItem>;
  openSuccessModal: () => void;
}

export const More: React.FC<MoreProps> = ({ row, openSuccessModal }: MoreProps) => {
  const isBriefOwner = isCurrentUser(row.original.createdBy.id);
  const openDrawerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { currency, rollouts } = useInitialProps();
  const inCrowsBriefEditRollout = Boolean(rollouts?.inCrowsBriefEditRollout);
  const inCrowsBriefDuplicateRollout = Boolean(rollouts?.inCrowsBriefDuplicateRollout);
  const briefIsActive = isBriefActive(row.original.briefDisplayStatus);
  const shouldShowEditButton = inCrowsBriefEditRollout && isBriefOwner && briefIsActive;
  const shouldShowDuplicateButton = inCrowsBriefDuplicateRollout && isBriefOwner && briefIsActive;
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isDuplicateDrawerOpen, setIsDuplicateDrawerOpen] = useState(false);

  const { data: enrichedData, isLoading: isEnrichedLoading } = useEnrichedBuyerBrief({
    briefId: row.original.id,
    enabled: isEditDrawerOpen,
    onError: () => {
      setIsEditDrawerOpen(false);
    },
  });
  const shouldShowEditDrawer = isEditDrawerOpen && !isEnrichedLoading && enrichedData?.brief;

  const { data: enrichedDuplicateData, isLoading: isDuplicateLoading } = useEnrichedDuplicateBrief({
    briefId: row.original.id,
    enabled: isDuplicateDrawerOpen,
    onError: () => {
      setIsDuplicateDrawerOpen(false);
      handleDuplicateError();
    },
  });

  const shouldShowDuplicateDrawer = isDuplicateDrawerOpen && !isDuplicateLoading && enrichedDuplicateData?.brief;
  const BriefDrawer = useBriefDrawer();

  const closeAction = getCloseAction({
    offersCount: row.original.offersCount,
    hiredCount: row.original.hiredCount,
  });

  const onCloseBriefSuccessHandler = (closeAction: CloseAction) => {
    metric.count(`close_brief.${closeAction}.success`);
    queryClient.invalidateQueries(BRIEFS_PAGINATED_LIST_QUERY_KEY);
    if (closeAction === CloseAction.DELETE_BRIEF) {
      raiseToast({
        message: appTranslate('brief_deleted_toast'),
        type: 'success',
        location: 'top',
      });
    }
  };

  const { isModalOpen, setIsModalOpen, onConfirmHandler, onCloseHandler } = useCloseBriefModal({
    briefId: row.original.id,
    closeAction,
    onSuccess: onCloseBriefSuccessHandler,
    onError: (closeAction: CloseAction) => {
      metric.count(`close_brief.${closeAction}.error`);
    },
    source: PAGE_NAME,
  });

  const handleViewBrief = () => {
    openDrawerRef.current?.click();
    sendBiEvent('view_full_brief');
  };

  const handleEditBrief = () => {
    setIsEditDrawerOpen(true);
    sendBiEvent('edit_brief');
  };

  const handleDuplicateBrief = () => {
    setIsDuplicateDrawerOpen(true);
  };

  const handleDuplicateError = useCallback(() => {
    raiseToast({
      message: appTranslate('something_went_wrong'),
      type: 'error',
      location: 'top',
    });
  }, []);

  const handleEditSuccess = useCallback(() => {
    queryClient.invalidateQueries(BRIEFS_PAGINATED_LIST_QUERY_KEY);
    queryClient.removeQueries(['EnrichedBuyerBrief', row.original.id]);
    setIsEditDrawerOpen(false);
    openSuccessModal();
  }, [queryClient, row.original.id, openSuccessModal]);

  const handleEditError = useCallback(() => {
    raiseToast({
      message: appTranslate('something_went_wrong'),
      type: 'error',
      location: 'top',
    });
  }, []);

  const handleDuplicateClose = useCallback(() => {
    queryClient.invalidateQueries(BRIEFS_PAGINATED_LIST_QUERY_KEY);
    setIsDuplicateDrawerOpen(false);
  }, [queryClient]);

  const sendBiEvent = (name: string) => reportBriefCardClick(name, row.original);
  const onActionsMenuClick = () => {
    if (!isActionsMenuOpen) {
      sendBiEvent('actions_menu');
    }
  };

  return (
    <Container width="fit-content">
      <Dropdown position="bottom-end" onOpenChanged={setIsActionsMenuOpen}>
        <DropdownAnchor fullWidth>
          <MoreOptionsWrapper
            shouldShowDuplicateButton={shouldShowDuplicateButton}
            shouldShowEditButton={shouldShowEditButton}
            isFirstRow={row.index === 0}
          >
            <IconButton
              aria-label="more"
              intent="primary"
              variant="ghost"
              marginLeft="auto"
              onClick={onActionsMenuClick}
            >
              <EllipsisIcon />
            </IconButton>
          </MoreOptionsWrapper>
        </DropdownAnchor>
        <DropdownContent appendToBody>
          <DropdownMenu>
            {shouldShowEditButton && (
              <MenuItem onClick={handleEditBrief}>
                <AppI18n k="actions.edit_brief" />
              </MenuItem>
            )}
            {shouldShowDuplicateButton && (
              <MenuItem onClick={handleDuplicateBrief}>
                <AppI18n k="actions.duplicate_brief" />
              </MenuItem>
            )}
            <MenuItem onClick={handleViewBrief}>
              <AppI18n k="actions.view_full_brief" />
            </MenuItem>
            {isBriefOwner && briefIsActive && (
              <CloseBriefButton
                closeAction={closeAction}
                onClick={() => {
                  setIsModalOpen(true);
                  sendBiEvent('change_brief_status');
                }}
              />
            )}
          </DropdownMenu>
        </DropdownContent>
      </Dropdown>
      <BriefDrawer
        briefId={row.original.id}
        currency={currency}
        hideFooter
        onEdit={shouldShowEditButton ? handleEditBrief : undefined}
        onDuplicate={shouldShowDuplicateButton ? handleDuplicateBrief : undefined}
        onDelete={
          isBriefOwner && isBriefActive(row.original.briefDisplayStatus)
            ? () => {
                setIsModalOpen(true);
                sendBiEvent('change_brief_status');
              }
            : undefined
        }
      >
        <Container display="none" ref={openDrawerRef} />
      </BriefDrawer>
      {shouldShowEditDrawer && (
        <EditBriefDrawer
          briefId={row.original.id}
          brief={enrichedData.brief}
          currency={currency}
          briefClassification={enrichedData.briefClassification}
          {...(enrichedData.extraWaveStatus && { extraWaveStatus: enrichedData.extraWaveStatus })}
          isOpen={isEditDrawerOpen}
          onClose={() => setIsEditDrawerOpen(false)}
          onSuccess={handleEditSuccess}
          onError={handleEditError}
        />
      )}
      {shouldShowDuplicateDrawer && (
        <EditBriefDrawer
          brief={enrichedDuplicateData.brief}
          currency={currency}
          briefClassification={enrichedDuplicateData.briefClassification}
          {...(enrichedDuplicateData.extraWaveStatus && { extraWaveStatus: enrichedDuplicateData.extraWaveStatus })}
          isOpen={isDuplicateDrawerOpen}
          onClose={handleDuplicateClose}
          onError={handleDuplicateError}
          headerKey="drawer_header_editor"
        />
      )}
      <CloseBriefModal
        isOpen={isModalOpen}
        onClose={onCloseHandler}
        onConfirm={onConfirmHandler}
        closeAction={closeAction}
      />
    </Container>
  );
};
